/**
 * The board's brand ramp, derived from the organisation's own colour.
 *
 * The approved mockup re-themes the whole board from a single organisation
 * hex - the hero gradient, the bar fills, the quick-action tints. Rather than
 * hard-code one palette, the same derivation runs here and the result is handed
 * to the board as CSS variables, so an org that changes its colour in Org
 * Settings gets a board that follows.
 *
 * Ported from applyOrg() in the mockup; the stop percentages are its.
 */

const FALLBACK = "#6941C6";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToHsl = (hex) => {
  const clean = String(hex || "").replace("#", "").slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(clean)) return null;

  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === r) h = (g - b) / delta + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
  }

  return [h, s * 100, l * 100];
};

const hsl = (h, s, l) =>
  `hsl(${Math.round(h)},${Math.round(clamp(s, 0, 100))}%,${Math.round(
    clamp(l, 0, 100)
  )}%)`;

/**
 * Returns the CSS custom properties the board's stylesheet reads. Spread onto
 * the root element's `style`.
 */
export const boardTheme = (orgColor) => {
  const hex = /^#?[0-9a-f]{6}$/i.test(String(orgColor || "")) ? orgColor : FALLBACK;
  const parsed = hexToHsl(hex) || hexToHsl(FALLBACK);
  const [h, s, l] = parsed;

  return {
    "--b-primary": hex,
    "--b-primary-25": hsl(h, Math.min(s, 92), 98.5),
    "--b-primary-50": hsl(h, Math.min(s, 92), 97),
    "--b-primary-200": hsl(h, Math.min(s, 88), 86),
    "--b-primary-300": hsl(h, Math.min(s, 86), Math.min(l + 22, 84)),
    "--b-primary-700": hsl(h, s, Math.max(l - 11, 18)),
    // The gradient runs dark -> the org's own colour -> light, left to right.
    "--b-hero-a": hsl(h, Math.min(s + 4, 100), Math.max(l - 16, 16)),
    "--b-hero-b": hex,
    "--b-hero-c": hsl(h, Math.min(s, 96), Math.min(l + 14, 80)),
  };
};

/** "r,g,b" for the organisation's colour, for use inside rgba(). */
export const boardTint = (orgColor) => {
  const hex = /^#?[0-9a-f]{6}$/i.test(String(orgColor || "")) ? orgColor : FALLBACK;
  const clean = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(clean.slice(i, i + 2), 16)).join(",");
};

export default boardTheme;
